import React, { useEffect, useState, useCallback } from "react";
import "./Admin.css";
import NouveauClients from "./NouveauClients/NouveauClients";
import NavigationTab from "./NavigationTab/NavigationTab";
import CreateAgent from "./Settings/CreateAgent/CreateAgent";
import CreateOperation from "./Settings/CreateOperation/CreateOperation";
import CreateRole from "./Settings/CreateRole/CreateRole";
import AgentCalendar from "./Calendar/AgentCalendar/AgentCalendar";
import Clients from "./Clients/Clients";
import CreatePartenaire from "./Settings/CreatePartenaire/CreatePartenaire";
import CreateForm from "./Settings/CreateForm/CreateForm";
import Archive from "./Archive/Archive";
import RecentMissions from "./RecentMissions/RecentMissions";
import NouveauClientUnitaire from "./NouveauClients/NouveauClientUnitaire/NouveauClientUnitaire";
import Payments from "./Payments/Payments";
import Billing from "./Billing/Billing";
import Statistics from "./Statistics/Statistics";

import "../Utils/Commons/Forms/FormLabeledText/FormLabeledText.css";
import { useAuth } from "../Security/AuthProvider";
import { usePermissions } from "../Utils/usePermissions";
import FeatureWrapper from "../Utils/FeatureWrapper";

const Admin = () => {
  const { getCredentials, poolConfig, admin } = useAuth();
  const credentials = getCredentials();
  const [agents, setAgents] = useState([]);
  const [currentPage, setCurrentPage] = useState("clients"); // Page par défaut
  const [currentAgent, setCurrentAgent] = useState(null);
  const [showNewClients, setShowNewClients] = useState(false);
  const [forms, setForms] = useState([]);
  
  // Utiliser le hook de permissions
  const { hasFeature, getCurrentPlan, loading: permissionsLoading } = usePermissions(credentials?.company_name);
  
  // Fonction fetchAgents unique et stable avec useCallback
  const fetchAgents = useCallback(async () => {
    try {
      // Vérifier que nous avons les credentials nécessaires
      if (!poolConfig?.company_name || !admin?.company_password) {
        console.log("Missing credentials for fetchAgents");
        return;
      }

      const response = await fetch("/agents/get-all", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          company_name: poolConfig.company_name,
          company_password: admin.company_password,
        }),
      });
      const data = await response.json();
      if (Array.isArray(data)) {
        setAgents(data);
      } else {
        setAgents([]);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
      setAgents([]);
    }
  }, [poolConfig?.company_name, admin?.company_password]);

  useEffect(() => {
    if (poolConfig?.company_name && admin?.company_password) {
      fetchAgents();
    }
  }, [fetchAgents, poolConfig?.company_name, admin?.company_password]);

  // Afficher un loader pendant le chargement des permissions
  if (permissionsLoading) {
    return (
      <div className="admin-container">
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Chargement de l'interface...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      {agents && Array.isArray(agents) && (
        <>
          <NavigationTab
            setCurrentPage={setCurrentPage}
            agentsNames={agents.map((agent) => agent.name)}
            setCurrentAgent={setCurrentAgent}
            setShowNewClients={setShowNewClients}
          />
          <div className="admin-content">
            {currentPage === "calendar" && currentAgent && (
              <AgentCalendar currentAgent={currentAgent} />
            )}

            {currentPage === "agents" && (
              <CreateAgent
                fetchAgents={fetchAgents}
                agents={agents}
                setAgents={setAgents}
              />
            )}
            
            {currentPage === "operations" && <CreateOperation />}
            {currentPage === "partenaires" && <CreatePartenaire />}
            {currentPage === "roles" && <CreateRole />}

            {/* Formulaires - uniquement si autorisé */}
            {currentPage === "forms" && (
              <FeatureWrapper 
                feature="form_creation"
                showUpgradeMessage={true}
              >
                <CreateForm />
              </FeatureWrapper>
            )}

            {/* Nouveau client unitaire - toujours autorisé */}
            {showNewClients && currentPage === "nouveauClientUnitaire" && (
              <NouveauClientUnitaire setShowNewClients={setShowNewClients} />
            )}
            
            {/* Import CSV - uniquement si bulk operations autorisé */}
            {showNewClients && currentPage === "nouveauClientsCSV" && (
              <FeatureWrapper 
                feature="bulk_operations"
                showUpgradeMessage={true}
              >
              <NouveauClients setShowNewClients={setShowNewClients} />
              </FeatureWrapper>
            )}

            {currentPage === "clients" && <Clients />}
            {currentPage === "recentMissions" && <RecentMissions />}
            {currentPage === "archive" && <Archive />}
            
            {/* Statistiques - uniquement pour Premium */}
            {currentPage === "statistics" && (
              <FeatureWrapper 
                feature="ai_insights"
                showUpgradeMessage={true}
              >
                <Statistics />
              </FeatureWrapper>
            )}
            
            {/* Facturation - uniquement pour les stats avancées */}
            {currentPage === "billing" && (
              <FeatureWrapper 
                feature="advanced_stats"
                showUpgradeMessage={true}
              >
                <Billing />
              </FeatureWrapper>
            )}
            
            {/* Paiements - uniquement pour les stats avancées */}
            {currentPage === "payments" && (
              <FeatureWrapper 
                feature="advanced_stats"
                showUpgradeMessage={true}
              >
                <Payments />
              </FeatureWrapper>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Admin;
